import React, { useContext, useEffect, useMemo, useState } from 'react'
import { makeStyles, tokens, Combobox, Option, mergeClasses } from '@fluentui/react-components'
import { UseFormReturn } from 'react-hook-form'
import { UpsertProcessOwnerDto, UserAccount } from '../../api/schemas/schema'
import { Person20Regular } from '@fluentui/react-icons'
import { getFullName } from '../../helpers/accountHelper'
import { Field } from '../common/inputs/Field'
import { Text } from '../common/text/Text'
import { t } from '../../i18n/i18n'
import { DataContext } from '../../context/DataContext'

const useStyles = makeStyles({
   comboBox: {
      width: '100%',
   },
   icon: {
      position: 'absolute',
      zIndex: 1,
      marginLeft: tokens.spacingHorizontalMNudge,
      marginTop: tokens.spacingVerticalMNudge,
   },
   proccessFieldIconPadding: {
      '& input': {
         marginLeft: tokens.spacingHorizontalXL,
      },
   },
   flex: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacingHorizontalS,
   },
})

type ProcessOwnerPickerProps = { processOwnerForm: UseFormReturn<UpsertProcessOwnerDto, any, UpsertProcessOwnerDto>; disabled?: boolean; required?: boolean }

export const ProcessOwnerPicker = ({ processOwnerForm, required, disabled }: ProcessOwnerPickerProps) => {
   const classes = useStyles()
   const {
      setValue,
      watch,
      clearErrors,
      formState: { errors },
   } = processOwnerForm
   const userAccount = watch('userAccount')
   const ownerFreeText = watch('ownerFreeText')
   const dataContext = useContext(DataContext)
   const { userAccounts } = dataContext.state
   useEffect(() => {
      const resetValue = userAccount ? getFullName(userAccount) : (ownerFreeText ?? '')
      setInput(resetValue)
   }, [userAccount, ownerFreeText])

   const initial = userAccount ? getFullName(userAccount) : (ownerFreeText ?? '')
   const [input, setInput] = useState(initial)

   const filteredOptions = useMemo(() => {
      if (!input) return userAccounts
      return userAccounts.filter((ua) => getFullName(ua).toLowerCase().startsWith(input.toLowerCase()))
   }, [input, userAccounts])

   processOwnerForm.register('userAccount', {
      validate: () => {
         const ua = processOwnerForm.getValues('userAccount')
         const text = processOwnerForm.getValues('ownerFreeText')
         return ua || text ? true : t('RequiredField')
      },
   })

   processOwnerForm.register('ownerFreeText')

   // When the input exactly matches a user, select that user
   useEffect(() => {
      const match = userAccounts.find((ua) => getFullName(ua).toLowerCase() === input.toLowerCase())
      if (match) selectUser(match)
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [input])

   const resetToFreeText = (value: string) => {
      setInput(value)
      setValue('userAccount', null, { shouldValidate: true })
      setValue('userAccountId', null)
      setValue('ownerFreeText', value, { shouldDirty: true, shouldValidate: true })
      clearErrors('userAccount')
   }

   const selectUser = (selected: UserAccount) => {
      setInput(getFullName(selected))
      setValue('userAccount', selected, { shouldDirty: true, shouldValidate: true })
      setValue('userAccountId', selected.id)
      setValue('ownerFreeText', null, { shouldValidate: true })
      clearErrors('userAccount')
   }

   return (
      <Field
         label={t('ProcessOwner')}
         className={mergeClasses(userAccount ? classes.proccessFieldIconPadding : '')}
         validationMessage={errors.userAccount ? errors.userAccount.message : ''}
         validationState={errors.userAccount ? 'error' : null}
         required={required}
      >
         <>
            <div>{userAccount && <Person20Regular className={classes.icon} />}</div>
            <Combobox
               disabled={disabled}
               className={classes.comboBox}
               value={input}
               freeform
               onChange={(e) => {
                  const value = e.target.value
                  resetToFreeText(value)
               }}
               onOptionSelect={(e, data) => {
                  const selected = userAccounts.find((ua) => ua.id === data.optionValue)
                  if (!selected) return
                  selectUser(selected)
               }}
            >
               {filteredOptions.map((account) => {
                  const fullName = getFullName(account)
                  return (
                     <Option key={account.id} value={account.id} text={fullName}>
                        <span className={classes.flex}>
                           <Person20Regular />
                           <Text>{fullName}</Text>
                        </span>
                     </Option>
                  )
               })}
            </Combobox>
         </>
      </Field>
   )
}
